'use client';

import type { OrganizedIdeas, IdeaTheme, IdentifiedGap, IdeaThemeType } from '@/lib/foundry/types';

interface IdeaOrganizerProps {
  organized: OrganizedIdeas;
  onProceedToQuestions: () => void;
  onBackToBrainDump: () => void;
}

const THEME_COLORS: Record<IdeaThemeType, string> = {
  problem: 'border-red-500/40 bg-red-500/10',
  solution: 'border-green-500/40 bg-green-500/10',
  target_customer: 'border-blue-500/40 bg-blue-500/10',
  market: 'border-purple-500/40 bg-purple-500/10',
  revenue: 'border-yellow-500/40 bg-yellow-500/10',
  competition: 'border-accent/40 bg-accent-muted',
  advantage: 'border-teal-500/40 bg-teal-500/10',
  resource: 'border-indigo-500/40 bg-indigo-500/10',
  risk: 'border-pink-500/40 bg-pink-500/10',
  other: 'border-border bg-cream/5',
};

const THEME_ICONS: Record<IdeaThemeType, string> = {
  problem: '🎯',
  solution: '💡',
  target_customer: '👥',
  market: '📊',
  revenue: '💰',
  competition: '⚔️',
  advantage: '🛡️',
  resource: '🔧',
  risk: '⚠️',
  other: '📝',
};

function ThemeCard({ theme }: { theme: IdeaTheme }) {
  return (
    <div className={`p-4 rounded-lg border ${THEME_COLORS[theme.type]}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-foreground flex items-center gap-2">
          <span>{THEME_ICONS[theme.type]}</span>
          {theme.label}
        </h4>
        <div className="flex items-center gap-1">
          <div className="h-1.5 w-16 bg-cream/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full"
              style={{ width: `${theme.confidence * 100}%` }}
            />
          </div>
          <span className="text-xs text-foreground-muted">{Math.round(theme.confidence * 100)}%</span>
        </div>
      </div>
      <ul className="space-y-2">
        {theme.ideas.map((idea, i) => (
          <li key={i} className="text-sm text-foreground-secondary flex items-start gap-2">
            <span className="text-accent mt-0.5">•</span>
            <span>{idea}</span>
          </li>
        ))}
      </ul>
      {theme.insights && (
        <p className="mt-3 pt-3 border-t border-border-muted text-xs text-foreground-tertiary italic">
          {theme.insights}
        </p>
      )}
    </div>
  );
}

function GapCard({ gap }: { gap: IdentifiedGap }) {
  const importanceColors = {
    critical: 'border-red-500/60 bg-red-500/20 text-red-300',
    important: 'border-yellow-500/60 bg-yellow-500/20 text-yellow-300',
    nice_to_have: 'border-border/30 bg-cream/10 text-foreground-secondary',
  };

  return (
    <div className="p-4 rounded-lg border border-border bg-background/40">
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold text-foreground">{gap.area}</h4>
        <span className={`text-xs px-2 py-0.5 rounded ${importanceColors[gap.importance]}`}>
          {gap.importance.replace('_', ' ')}
        </span>
      </div>
      <p className="text-sm text-foreground-secondary mb-3">{gap.description}</p>
      <div className="text-xs text-foreground-tertiary">
        <span className="font-medium">Example questions:</span>
        <ul className="mt-1 space-y-1">
          {gap.suggested_questions.slice(0, 2).map((q, i) => (
            <li key={i} className="text-foreground-muted">• {q}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function IdeaOrganizer({ organized, onProceedToQuestions, onBackToBrainDump }: IdeaOrganizerProps) {
  const criticalGaps = organized.gaps.filter((g) => g.importance === 'critical');
  const hasGaps = organized.gaps.length > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">Ideas Organized</h2>
        <p className="text-foreground-tertiary">
          The AI has analyzed your ideas and grouped them into themes
        </p>
      </div>

      {/* Summary */}
      <div className="p-4 bg-accent-muted border border-border rounded-lg">
        <p className="text-foreground">{organized.summary}</p>
      </div>

      {/* Themes Grid */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Identified Themes</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {organized.themes.map((theme, i) => (
            <ThemeCard key={i} theme={theme} />
          ))}
        </div>
      </div>

      {/* Patterns */}
      {organized.patterns.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Patterns & Insights</h3>
          <div className="space-y-2">
            {organized.patterns.map((pattern, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-background/40 rounded-lg">
                <span className="text-accent">✨</span>
                <p className="text-sm text-foreground-secondary">{pattern}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connections */}
      {organized.connections.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Connections</h3>
          <div className="space-y-2">
            {organized.connections.map((conn, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-foreground-secondary p-2 bg-background/30 rounded">
                <span className="font-medium text-foreground">{conn.from}</span>
                <span className="text-accent">→</span>
                <span className="text-foreground-tertiary">{conn.relationship}</span>
                <span className="text-accent">→</span>
                <span className="font-medium text-foreground">{conn.to}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gaps */}
      {hasGaps && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <span>Areas to Explore</span>
            {criticalGaps.length > 0 && (
              <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded">
                {criticalGaps.length} critical
              </span>
            )}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {organized.gaps.map((gap, i) => (
              <GapCard key={i} gap={gap} />
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border-muted">
        <button
          onClick={onBackToBrainDump}
          className="px-4 py-2 text-foreground-tertiary hover:text-foreground transition-colors"
        >
          ← Add more ideas
        </button>
        <button
          onClick={onProceedToQuestions}
          className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
        >
          {hasGaps ? 'Answer Questions to Fill Gaps' : 'Generate Canvas'}
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );
}
